import ReactDOM from 'react-dom'
import ConsentCookie from 'scripts/lib/ConsentCookie'
import lscache from 'lscache'
import { AllHtmlEntities } from 'html-entities'
import uaDeviceDetector from 'ua-device-detector'
import cookie from 'react-cookies'
import { generateUUID } from 'scripts/helpers/Utils'

const MINUTE = 60
const HOUR = MINUTE * 60
const DAY = HOUR * 24

const WEEK = 7
const MONTH = 31

const toMins = diff => Math.floor(diff / MINUTE)
const toHours = diff => Math.floor(diff / HOUR)
const toWeeks = days => Math.ceil(days / WEEK)

const isValidDate = (date) => {
  return date instanceof Date && !isNaN(date)
}

export const dateDiff = date => {
  if (!isValidDate(date)) { return 0 }
  return (((new Date()).getTime() - date.getTime()) / 1000)
}

export const prettyDate = (time, locale, i18n) => {
  if (!time) { return null }

  var date = new Date((time || ''))
  var diff = dateDiff(date)
  var dayDiff = Math.floor(diff / DAY)

  if (isNaN(dayDiff) || dayDiff < 0 || dayDiff >= 31 || locale) {
    const options = { month: 'short', day: 'numeric' }
    if (date.getFullYear() < (new Date().getFullYear()) || locale) {
      options.year = 'numeric'
    }

    return date.toLocaleDateString('en-US', options)
  }

  const pluralise = (number, str) => {
    return i18n.formatString(i18n.dates[`${str}${number > 1 ? 's' : ''}`], number)
  }

  let pretty = ''
  if (diff < MINUTE) {
    pretty = i18n.dates.justNow
  } else if (diff < HOUR) {
    pretty = pluralise(toMins(diff), 'min')
  } else if (diff < DAY) {
    pretty = pluralise(toHours(diff), 'hour')
  } else if (diff < DAY * 2) {
    pretty = i18n.dates.yesterday
  } else if (dayDiff < WEEK) {
    pretty = pluralise(dayDiff, 'day')
  } else if (dayDiff < MONTH) {
    pretty = pluralise(toWeeks(dayDiff), 'week')
  }

  return pretty
}

export const decodeHTML = (string) => {
  if (!string) { return '' }
  if (typeof window === 'undefined') { return string }
  const entities = new AllHtmlEntities()
  return entities.decode(string)
}

export function setTransformAnimation(element, position, durationMs = 0) {
  const prefixes = ['Webkit', 'Moz', 'ms', 'O', '']

  for (let value of prefixes) {
    element.style[value + 'Transition'] = `transform ${durationMs}ms ease-out`
    element.style[value + 'Transform'] = position ? `translate3d(${position}px, 0, 0)` : null
  }
}

export const extractSubType = (associations, subtype) => {
  if (!associations) { return null }
  return associations.filter((item, index) => {
    if (item.subtype === subtype || item.type === subtype) { return true }
  })
}

export const toTitleCase = (str) => {
  if (!str) { return '' }
  return str.replace(/\w\S*/g, (txt) => {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
  })
}

export const StripTags = (content) => {
  var regex = /(<([^>]+)>)|(\n\r?|\r)/ig
  var oddCharacter = '[…]'
  return content.replace(regex, '').replace(oddCharacter, '').trim()
}

export const makeCancellablePromise = function (promise) {
  // Set flag init
  let cancelled = false

  // Wrap the promise in a cancel-flag-checking closure
  return {
    promise: new Promise((resolve, reject) => {
      promise.then((val) => {
        cancelled ? reject(new Error('Request Cancelled')) : resolve(val)
      }).catch((err) => {
        cancelled ? reject(new Error('Request Cancelled')) : reject(err)
      })
    }),
    cancel: () => {
      cancelled = true
    }
  }
}

export const DOMElem = (node, selector) => {
  const DOM = ReactDOM.findDOMNode(node)
  if (selector) {
    return DOM.querySelectorAll(selector)
  }
  return DOM
}

export const lazyLoader = (domrefs) => {
  Array.from(DOMElem(domrefs, 'img,iframe')).forEach((item) => {
    item.classList.add('lazyload')
    if (item.src !== '') {
      item.setAttribute('data-src', item.src)
    }
    if (item.srcset) {
      item.setAttribute('data-srcset', item.srcset)
      item.srcset = ''
    }
  })
}

export function insertScript(src, id, parentElement) {
  const script = window.document.createElement('script')
  script.async = true
  script.src = src
  script.id = id
  parentElement.appendChild(script)

  return script
}

export function removeScript(id, parentElement) {
  const script = window.document.getElementById(id)
  if (script) {
    parentElement.removeChild(script)
  }
}

export const eventTriggerGA = (newState, addedTags, removedTags) => {
  if (Object.keys(addedTags).length === 0) { return null }
  const consent = new ConsentCookie()
  if (consent.hasConsent('analytics') && process.env.NODE_ENV === 'production') {
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({
      'event': 'pageview',
      'pageTitle': newState.title
    })
  } else {
    console.log('PV: ' + window.location)
  }
}

export const isClient = () => {
  return (typeof window !== 'undefined')
}

export const isMobile = () => {
  const MOBILE_BREAK = 970
  return isClient() ? (window.innerWidth < MOBILE_BREAK) : false
}

export const authorInfo = (author, disambiguate) => {
  if (!author) { return null }
  const {displayName, firstName, lastName} = author
  if (firstName && lastName) {
    const formattedName = firstName.substr(0, 1).toUpperCase() + '. ' + toTitleCase(lastName)
    return (disambiguate ? `${formattedName} (${displayName})` : formattedName)
  }
  return displayName || ''
}

export const isDarkmode = () => lscache.get('darkmode')

export const renderDarkMode = () => {
  const body = document.getElementById('body-element')
  if (isDarkmode()) {
    body.classList.add('darkmode')
  } else {
    body.classList.remove('darkmode')
  }
}

export const toggleDarkModeAndRender = () => {
  // TODO: important to check if user is logged in or not. If they are not logged in,
  // do not apply this logic
  lscache.set('darkmode', !isDarkmode())
  renderDarkMode()
}

export const getRandomString = () => {
  let result = ''
  while (!result) {
    result = Math.random().toString(36).substring(2)
  }
  return result
}
export const getDevice = () => {
  if (!isClient()) { return null }
  const detector = uaDeviceDetector.parseUserAgent(navigator.userAgent)
  if (detector.isDesktop()) {
    return 'desktop'
  } else if (detector.isTablet()) {
    return 'tablet'
  } else if (detector.isMobile()) {
    return 'mobile'
  }
  return 'unknown'
}
export const generateSessionCookie = () => {
  const sessionId = cookie.load('session-cookie') || generateUUID()
  const expires = new Date()
  expires.setMinutes(expires.getMinutes() + 30)
  cookie.save('session-cookie', sessionId, {path: '/', secure: false, httpOnly: false, expires: expires})
  return sessionId
}
export const getBuildHash = () => {
  if (!isClient()) { return null }
  return window.app.hash || 'unknown'
}

export const getUserInfo = (lang) => {
  const consent = new ConsentCookie()
  const userInfo = cookie.load('user-info')
  if (consent.hasConsent('analytics') && process.env.NODE_ENV === 'production') {
    return typeof userInfo !== 'undefined' ? userInfo[lang] : null
  }
  return null
}



// WEBPACK FOOTER //
// ./assets/js/lib/utils.js